import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, MessageSquare } from "lucide-react";
import SectionDivider from "@/components/SectionDivider";
import SunRays from "@/components/SunRays";
import AreaMarquee from "@/components/AreaMarquee";
import Starburst from "@/components/Starburst";
import HeroWindowAnimation from "@/components/HeroWindowAnimation";

export default function ImmersiveHero() {
  return (
    <>
      <section
        data-hero-parallax
        className="relative min-h-[100svh] md:min-h-[105vh] flex items-center justify-center overflow-hidden"
      >
        {/* Full-bleed animated background */}
        <HeroWindowAnimation />

        <SunRays
          variant="corner"
          className="top-0 right-0 w-[250px] h-[250px] md:w-[500px] md:h-[500px] opacity-30 z-[1]"
        />

        {/* Content */}
        <div
          className="container relative z-10 flex flex-col items-center text-center pt-36 pb-24 md:pt-44 md:pb-32"
          style={{
            transform:
              "translate3d(0, calc(var(--scroll-progress, 0) * -120px), 0) scale(calc(1 + var(--scroll-progress, 0) * 0.15))",
            opacity:
              "max(0, calc(1 - var(--scroll-progress, 0) * 2.5))" as unknown as number,
            willChange: "transform, opacity",
          }}
        >
          <h1 className="font-black leading-[0.95] text-primary-foreground mb-3 relative">
            <span
              className="hidden md:block absolute -top-6 -right-10 lg:-right-16 sticker-tilt-r pointer-events-none"
              aria-hidden
            >
              <Starburst size="sm">FREE<br />QUOTE</Starburst>
            </span>

            <span className="block text-[2.6rem] sm:text-6xl md:text-7xl lg:text-8xl tracking-tight text-shadow-pop-dark">
              KEEP YOUR
            </span>
            <span className="block text-[2.6rem] sm:text-6xl md:text-7xl lg:text-8xl tracking-tight text-shadow-pop-dark">
              HOME
            </span>
            <span
              className="block text-sun-yellow text-[2.8rem] sm:text-6xl md:text-[5rem] lg:text-[6.5rem] tracking-tight mt-1"
              style={{
                textShadow:
                  "4px 4px 0px hsl(174 72% 22%), 0 0 40px hsl(45 100% 55% / 0.3)",
                WebkitTextStroke: "1px hsl(174 72% 30% / 0.3)",
              }}
            >
              COOL & COMFORTABLE
            </span>
          </h1>

          <p className="text-primary-foreground/80 text-base md:text-lg font-bold tracking-wide mb-10 max-w-md">
            Custom Sun Screens · Professionally Installed
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
            <Button
              variant="hero"
              size="xl"
              className="glow-pulse text-lg py-6 px-10 font-extrabold tracking-wide"
              asChild
            >
              <Link to="/instant-quote">
                INSTANT QUOTE
                <ArrowRight className="w-5 h-5 ml-1" />
              </Link>
            </Button>
            <div className="flex gap-3 w-full sm:w-auto">
              <Button
                variant="hero-outline"
                size="xl"
                className="text-lg py-6 px-6 font-extrabold tracking-wide flex-1 sm:flex-initial"
                asChild
              >
                <a href="tel:+16233014595">
                  <Phone className="w-5 h-5 mr-1" />
                  CALL
                </a>
              </Button>
              <Button
                variant="hero-outline"
                size="xl"
                className="text-lg py-6 px-6 font-extrabold tracking-wide flex-1 sm:flex-initial"
                asChild
              >
                <a href="sms:+16233014595">
                  <MessageSquare className="w-5 h-5 mr-1" />
                  TEXT
                </a>
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-12 text-primary-foreground/80 text-xs font-bold tracking-widest uppercase">
            {[
              "Licensed & Insured",
              "Free Estimates",
              "Lowest Prices",
              "Locally Owned",
            ].map((text, i) => (
              <span key={text} className="flex items-center gap-1.5">
                {i > 0 && (
                  <span className="hidden sm:inline text-primary-foreground/30 mr-1.5">
                    ·
                  </span>
                )}
                <svg
                  className="w-3.5 h-3.5 text-sun-yellow"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {text}
              </span>
            ))}
          </div>
        </div>

      </section>

      <div className="bg-warm-white relative z-10">
        <AreaMarquee />
      </div>

      <SectionDivider variant="swoosh" fillClass="fill-warm-white" />
    </>
  );
}
